export const DISCOVERY_ATTRIBUTION_KEY = "finda.discovery-attribution.v1";
export const DISCOVERY_ATTRIBUTION_TTL = 30 * 60 * 1000;
export type DiscoverySource = "chatgpt" | "perplexity" | "claude" | "gemini" | "copilot" | "mcp" | "webmcp";
export interface DiscoveryAttribution { source: DiscoverySource; evidence: "link" | "referrer" | "native_tool"; started_at: number }
type Storage = Pick<globalThis.Storage, "getItem" | "setItem" | "removeItem">;
const sources: readonly DiscoverySource[] = ["chatgpt", "perplexity", "claude", "gemini", "copilot", "mcp", "webmcp"];
const hosts: Record<string, DiscoverySource> = {
  "chatgpt.com": "chatgpt", "chat.openai.com": "chatgpt", "perplexity.ai": "perplexity", "claude.ai": "claude",
  "gemini.google.com": "gemini", "copilot.microsoft.com": "copilot",
};

function sourceFromHost(host: string): DiscoverySource | undefined {
  return Object.entries(hosts).find(([allowed]) => host === allowed || host.endsWith(`.${allowed}`))?.[1];
}

/** Recognized attribution signals only. A link/referrer is a signal, not proof of agent identity. */
export function resolveDiscoveryAttribution(url: URL, referrer: string, stored: string | null, now = Date.now()): DiscoveryAttribution | null {
  const tag = url.searchParams.get("discovery_source") ?? url.searchParams.get("utm_source")?.toLowerCase();
  const source = tag && sources.includes(tag as DiscoverySource) ? tag as DiscoverySource : tag ? sourceFromHost(tag) : undefined;
  if (source) return { source, evidence: "link", started_at: now };
  if (referrer) {
    try {
      const previous = new URL(referrer);
      if (previous.origin !== url.origin) {
        const source = sourceFromHost(previous.hostname);
        return source ? { source, evidence: "referrer", started_at: now } : null;
      }
    } catch { /* A malformed referrer supplies no attribution. */ }
  }
  try {
    const value = JSON.parse(stored ?? "null");
    if (value && sources.includes(value.source) && ["link", "referrer", "native_tool"].includes(value.evidence)
      && Number.isFinite(value.started_at) && value.started_at <= now && now - value.started_at < DISCOVERY_ATTRIBUTION_TTL) return value;
  } catch { /* Malformed/expired storage must not affect browsing. */ }
  return null;
}

export function updateDiscoveryAttribution(storage: Storage | null, url: URL, referrer: string, nativeTool = false, now = Date.now()) {
  let stored: string | null = null;
  try { stored = storage?.getItem(DISCOVERY_ATTRIBUTION_KEY) ?? null; } catch { /* Storage may be disabled. */ }
  const attribution = nativeTool ? { source: "webmcp" as const, evidence: "native_tool" as const, started_at: now }
    : resolveDiscoveryAttribution(url, referrer, stored, now);
  try {
    if (attribution) storage?.setItem(DISCOVERY_ATTRIBUTION_KEY, JSON.stringify(attribution));
    else storage?.removeItem(DISCOVERY_ATTRIBUTION_KEY);
  } catch { /* Analytics must remain optional. */ }
  return attribution;
}

export function discoveryLink(url: string, source: "mcp"): string {
  const result = new URL(url);
  // Only tag our own destinations. Canonical citations and merchant URLs stay untouched.
  if (result.protocol === "https:" && ["www.finda.show", "finda.show"].includes(result.hostname)) result.searchParams.set("discovery_source", source);
  return result.toString();
}
